import React from 'react';
import useMedicalProfileFiles from '../../../../../hooks/api/useMedicalProfileFiles';
import { medicalProfileFilesService } from '../../../../../primary-record-types/apiTypes';
import { getIntl } from '../../../../../utils/intl';
import SearchResultCard from './SearchResultCard';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import FileCard from '../../../../shared/FileCard';
import FolderCard from '../../../../shared/FolderCard';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';

const intl = getIntl('SearchContent', {
    text: 'text',
    fuzzy: 'fuzzy',
    vector: 'semantic',
    noSearchResults: 'No search {searchType} results for "{search}"...',
    folders: 'Folders',
    files: 'Files',
    contents: 'Contents',
});

export type SearchContentOwnProps = {
    search: string;
    medicalProfileId: string;
    onFocusFolder: (folderId: string) => void;
};

export type SearchContentProps = SearchContentOwnProps;

const tabs: medicalProfileFilesService.SearchType[] = ['text', 'fuzzy', 'vector'];

const _chips = tabs.map((tab) => ({
    key: tab,
    label: tab,
}));

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const SearchContent: React.FC<SearchContentProps> = (props) => {
    const [activeList, setActiveList] = React.useState<medicalProfileFilesService.SearchType>('text');
    const { navigate } = useProfilesTabNavigation();
    const {
        isTextSearching,
        isFuzzySearching,
        isVectorSearching,
        textSearchResult,
        fuzzySearchResult,
        vectorSearchResult,
    } = useMedicalProfileFiles({
        medicalProfileId: props.medicalProfileId,
        search: props.search,
    });

    const getResultsForTab = (tab: medicalProfileFilesService.SearchType) => ({
        isLoading: tab === 'text' ? isTextSearching : tab === 'fuzzy' ? isFuzzySearching : isVectorSearching,
        results: tab === 'text' ? textSearchResult : tab === 'fuzzy' ? fuzzySearchResult : vectorSearchResult,
    });

    const getSearchResultCount = (tab: medicalProfileFilesService.SearchType): number => {
        const results = getResultsForTab(tab);
        return results.results && !results.isLoading
            ? results.results.fileContentsSearchResults.length +
                  results.results.fileNameSearchResults.length +
                  results.results.folderNameSearchResults.length
            : 0;
    };

    const getSearchResultCountText = (tab: medicalProfileFilesService.SearchType): string => {
        const results = getResultsForTab(tab);
        return results.results && !results.isLoading ? getSearchResultCount(tab).toString() : '...';
    };

    const renderResultSection = (sectionTitle: string, items: React.ReactElement[] | undefined) =>
        items?.length ? (
            <Stack vertical fullWidth space={contentStackSpacing}>
                <Text variant="h4">{sectionTitle}</Text>
                {items}
            </Stack>
        ) : null;

    return (
        <Stack vertical flex={1} fullWidth testID="action-button-layout-stack-container">
            <AnimateLoadedContent isLoading={isTextSearching && isFuzzySearching && isVectorSearching}>
                {() => (
                    <TabView
                        tabs={tabs.map<TabOption>((_) => ({
                            title: `${capitalize(intl(_))} (${
                                getResultsForTab(_).isLoading ? '...' : getSearchResultCountText(_)
                            })`,
                            value: _,
                            renderTabContent: () => (
                                <ActionButtonLayout scrollable>
                                    <MainContentContainer>
                                        <Stack vertical fullWidth space={8}>
                                            <>
                                                {renderResultSection(
                                                    intl('folders'),
                                                    getResultsForTab(_)
                                                        .results?.folderNameSearchResults.sort((a, b) => {
                                                            if (_ === 'text') {
                                                                return a.folderName.localeCompare(b.folderName);
                                                            } else {
                                                                return 0;
                                                            }
                                                        })
                                                        .map((folder) => (
                                                            <FolderCard
                                                                key={folder.medicalProfileFileFolderId}
                                                                folder={folder}
                                                                onPress={() =>
                                                                    props.onFocusFolder(
                                                                        folder.medicalProfileFileFolderId,
                                                                    )
                                                                }
                                                            />
                                                        )),
                                                )}
                                                {renderResultSection(
                                                    intl('files'),
                                                    getResultsForTab(_).results?.fileNameSearchResults.map((result) => (
                                                        <FileCard
                                                            key={result.medicalProfileFileId}
                                                            file={result}
                                                            onPress={() => {
                                                                navigate('file', {
                                                                    profileId: props.medicalProfileId,
                                                                    fileId: result.medicalProfileFileId,
                                                                });
                                                            }}
                                                        />
                                                    )),
                                                )}
                                                {renderResultSection(
                                                    intl('contents'),
                                                    getResultsForTab(_).results?.fileContentsSearchResults.map(
                                                        (result, index) => (
                                                            <SearchResultCard
                                                                key={`${result.medicalProfileFileArtifactId}-${index}`}
                                                                searchResult={result}
                                                                onPress={() => {
                                                                    const args: ProfilesTabParamList['file'] = {
                                                                        profileId: props.medicalProfileId,
                                                                        fileId: result.medicalProfileFileId,
                                                                        page: String(result.page),
                                                                    };

                                                                    if (result.type === 'image') {
                                                                        args.bbTop = String(result.boundingBox?.top);
                                                                        args.bbLeft = String(result.boundingBox?.left);
                                                                        args.bbWidth = String(
                                                                            result.boundingBox?.width,
                                                                        );
                                                                        args.bbHeight = String(
                                                                            result.boundingBox?.height,
                                                                        );
                                                                    }

                                                                    if (result.type === 'audio') {
                                                                        args.startTime = String(result.startTime);
                                                                    }

                                                                    navigate('file', args);
                                                                }}
                                                                medicalProfileId={props.medicalProfileId}
                                                            />
                                                        ),
                                                    ),
                                                )}
                                            </>
                                        </Stack>
                                    </MainContentContainer>
                                </ActionButtonLayout>
                            ),
                        }))}
                        selectedTab={activeList}
                        onChange={(_) => setActiveList(_ as medicalProfileFilesService.SearchType)}
                    />
                )}
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(SearchContent);
